import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Flag, Globe, Loader2, MessageCircle, Square, Volume2, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

const RATES = [0.5, 0.75, 1.0, 1.25, 1.5];

export const ReaderPagination = ({
  page, totalPages, hasMore = false, loadingMore = false,
  wordCount, newWords,
  sentenceMode, sentenceIndex, sentenceCount, part, parts,
  onPrevious, onNext, onFinish, finishLoading, ready,
  onPlaySentence, playing,
  playbackRate = 1.0, onRateChange,
  onTranslateSentence, sentenceTranslation, sentenceTranslating,
  onAskSentence,
}) => {
  const [showSpeedPicker, setShowSpeedPicker] = useState(false);

  return (
  <footer className="reader-pagination" data-testid="pagination-controls">

    {/* ── Translation result — inline, no absolute positioning ── */}
    {sentenceMode && sentenceTranslation && (
      <div
        className="w-full text-center text-sm italic text-foreground/80 bg-muted/30 border border-border/40 rounded-lg px-3 py-2"
        data-testid="sentence-translation"
      >
        {sentenceTranslation}
      </div>
    )}

    {/* ── Sentence action bar: audio · translate · ask ── */}
    {sentenceMode && (
      <div className="w-full grid grid-cols-3 gap-2" data-testid="sentence-action-bar">
        {/* Listen + speed picker */}
        <div className="relative">
          <div className={`flex items-stretch rounded-lg border transition-colors overflow-hidden
            ${playing
              ? 'bg-primary/10 border-primary/40 text-primary'
              : 'border-border/50 text-muted-foreground hover:text-foreground'
            }`}
          >
            {/* Play / Stop */}
            <button
              onClick={onPlaySentence}
              className="flex flex-col items-center justify-center gap-1 py-2 px-2 flex-1 hover:bg-muted/40 transition-colors"
              aria-label={playing ? 'Stop audio' : 'Listen'}
              data-testid="play-sentence-btn"
            >
              {playing ? <Square className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              <span className="text-[11px] font-medium leading-none">{playing ? 'Stop' : 'Listen'}</span>
            </button>
            {/* Speed toggle */}
            <button
              onClick={() => setShowSpeedPicker(p => !p)}
              className="flex flex-col items-center justify-center gap-0.5 px-1.5 border-l border-current/20 hover:bg-muted/40 transition-colors"
              aria-label="Playback speed"
              data-testid="sentence-speed-toggle"
            >
              <span className="text-[10px] font-semibold leading-none tabular-nums">
                {playbackRate === 1 ? '1×' : `${playbackRate}×`}
              </span>
              <ChevronDown className="h-3 w-3 opacity-60" />
            </button>
          </div>
          {/* Speed dropdown */}
          {showSpeedPicker && (
            <div className="absolute bottom-full left-0 mb-1 z-50 bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-1 min-w-[90px]" data-testid="sentence-speed-picker">
              {RATES.map(rate => (
                <button
                  key={rate}
                  onClick={() => { onRateChange?.(rate); setShowSpeedPicker(false); }}
                  className={`w-full text-left px-3 py-1.5 rounded text-xs transition-colors hover:bg-slate-700
                    ${playbackRate === rate ? 'text-amber-400 font-semibold' : 'text-slate-300'}`}
                  data-testid={`sentence-speed-${rate}`}
                >
                  {rate}×
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Translate */}
        <button
          onClick={onTranslateSentence}
          disabled={sentenceTranslating}
          className={`flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-lg border transition-colors
            ${sentenceTranslation
              ? 'bg-primary/10 border-primary/40 text-primary'
              : 'border-border/50 hover:bg-muted/40 text-muted-foreground hover:text-foreground'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          aria-label={sentenceTranslation ? 'Hide translation' : 'Translate'}
          data-testid="translate-sentence-btn"
        >
          {sentenceTranslating
            ? <Loader2 className="h-5 w-5 animate-spin" />
            : <Globe className="h-5 w-5" />
          }
          <span className="text-[11px] font-medium leading-none">
            {sentenceTranslating ? 'Translating…' : sentenceTranslation ? 'Hide' : 'Translate'}
          </span>
        </button>

        {/* Ask */}
        <button
          onClick={onAskSentence}
          className="flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-lg border border-border/50 hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Ask about this sentence"
          data-testid="ask-sentence-btn"
        >
          <MessageCircle className="h-5 w-5" />
          <span className="text-[11px] font-medium leading-none">Ask</span>
        </button>
      </div>
    )}

    {/* ── Navigation row ── */}
    <div className="w-full flex items-center justify-between gap-1">
      <Button
        variant="outline" size="sm" className="gap-1 px-2"
        disabled={!ready || page === 0}
        onClick={onPrevious}
        data-testid={sentenceMode ? 'prev-sentence-btn' : 'prev-page-btn'}
        aria-label="Previous"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="hidden min-[360px]:inline">Previous</span>
      </Button>

      <div className="min-w-0 text-center text-xs text-muted-foreground" aria-live="polite" data-testid="reader-page-info">
        <div>
          {sentenceMode
            ? `Sentence ${sentenceIndex + 1} of ${sentenceCount}${hasMore ? '+' : ''}`
            : `Page ${page + 1} of ${Math.max(1, totalPages)}${hasMore ? '+' : ''}`
          }
        </div>
        <div className="text-[10px]" data-testid="reader-page-detail">
          {sentenceMode
            ? (parts > 1 ? `Part ${part + 1} of ${parts}` : ' ')
            : `${wordCount} words${newWords ? ` · ${newWords} new` : ''}`
          }
        </div>
      </div>

      {page < totalPages - 1 || hasMore
        ? (
          <Button
            variant="outline" size="sm" className="gap-1 px-2"
            disabled={!ready || (loadingMore && page >= totalPages - 1)}
            onClick={onNext}
            data-testid={sentenceMode ? 'next-sentence-btn' : 'next-page-btn'}
            aria-label="Next"
          >
            <span className="hidden min-[360px]:inline">Next</span>
            {loadingMore && page >= totalPages - 1
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <ArrowRight className="h-4 w-4" />
            }
          </Button>
        )
        : (
          <Button
            size="sm" className="gap-1 px-2"
            onClick={onFinish}
            disabled={!ready || finishLoading}
            data-testid="finish-text-pagination-btn"
          >
            {finishLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flag className="h-4 w-4" />}
            Finish
          </Button>
        )
      }
    </div>

    {/* Progress bar — visible in both text mode and sentence mode */}
    <Progress
      value={sentenceMode
        ? (sentenceIndex + 1) / Math.max(1, sentenceCount) * 100
        : (page + 1) / Math.max(1, totalPages) * 100
      }
      className="absolute bottom-0 inset-x-0 h-0.5"
      data-testid="sentence-progress"
      aria-label="Reading progress"
    />
  </footer>
  );
};
